/**
 * BrandingContext - Multi-tenant theming and templating system
 * Provides template_id for selecting tenant-specific assets, colors, and copy
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { updateApiTemplateId } from '../services/api';

const BrandingContext = createContext();

// Available brand templates
const BRAND_TEMPLATES = {
  'feeling_froggy': {
    id: 'feeling_froggy',
    name: 'Feeling Froggy',
    description: 'Amphibian facility management and e-commerce',
    primaryColor: '#28a745',
    accentColor: '#3b82f6',
    logo: '/logos/feeling-froggy-logo.svg',
    favicon: '/favicon-froggy.ico',
    heroText: 'Welcome to Feeling Froggy',
    footerLinks: [
      { label: 'Shipping Policy', href: '/shipping-policy' },
      { label: 'Support', href: 'mailto:support@feelingfroggy.llc' }
    ]
  },
  'sienna_shirts': {
    id: 'sienna_shirts',
    name: 'Sienna Shirts',
    description: 'Custom apparel and merchandise',
    primaryColor: '#a0522d',
    accentColor: '#cd853f',
    logo: '/logos/sienna-shirts-logo.svg',
    favicon: '/favicon-sienna.ico',
    heroText: 'Welcome to Sienna Shirts',
    footerLinks: [
      { label: 'Size Guide', href: '/size-guide' },
      { label: 'Returns', href: '/returns' }
    ]
  }
};

// Default template
const DEFAULT_TEMPLATE_ID = 'feeling_froggy';

export const BrandingProvider = ({ children }) => {
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATE_ID);
  const [template, setTemplate] = useState(BRAND_TEMPLATES[DEFAULT_TEMPLATE_ID]);

  // Initialize template from environment or facility setting
  useEffect(() => {
    // Check environment variable first
    const envTemplateId = process.env.REACT_APP_BRAND_TEMPLATE_ID;
    if (envTemplateId && BRAND_TEMPLATES[envTemplateId]) {
      setTemplateId(envTemplateId);
      setTemplate(BRAND_TEMPLATES[envTemplateId]);
      return;
    }

    // TODO: Later, check per-facility setting from API
    // For now, use default
    console.debug('[BrandingContext] Using default template:', DEFAULT_TEMPLATE_ID);
  }, []);

  // Update CSS custom properties and API headers when template changes
  useEffect(() => {
    if (template) {
      document.documentElement.style.setProperty('--brand-primary', template.primaryColor);
      document.documentElement.style.setProperty('--brand-accent', template.accentColor);
      
      // Update favicon
      const favicon = document.querySelector('link[rel="icon"]') || document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = template.favicon;
      if (!document.querySelector('link[rel="icon"]')) {
        document.head.appendChild(favicon);
      }
      
      // Update API client to use this template ID
      updateApiTemplateId(templateId);
      
      console.debug('[BrandingContext] Applied template styles:', template.id);
    }
  }, [template, templateId]);

  const switchTemplate = (newTemplateId) => {
    if (BRAND_TEMPLATES[newTemplateId]) {
      setTemplateId(newTemplateId);
      setTemplate(BRAND_TEMPLATES[newTemplateId]);
      console.debug('[BrandingContext] Switched to template:', newTemplateId);
    }
  };

  const getTemplateAsset = (assetType, fallback = '') => {
    return template?.[assetType] || fallback;
  };

  const getTemplateCopy = (copyKey, fallback = '') => {
    return template?.[copyKey] || fallback;
  };

  // API header helper for backend requests
  const getApiHeaders = (existingHeaders = {}) => {
    return {
      ...existingHeaders,
      'X-Template-ID': templateId
    };
  };

  const value = {
    templateId,
    template,
    templates: BRAND_TEMPLATES,
    switchTemplate,
    getTemplateAsset,
    getTemplateCopy,
    getApiHeaders
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;